import { useQuery } from "@tanstack/react-query";
import { Card, Drawer, Typography } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiFetch, withQuery } from "../api/client";
import type {
  CircumscriptionContactRow,
  CoordinatorRow,
  ListResponse,
} from "../api/types";
import { DataTable } from "../components/DataTable";
import { FilterBar } from "../components/FilterBar";
import type { FilterValues } from "../components/FilterBar";
import { useCircumscriptionContactColumns, useCoordinatorColumns } from "../components/domainColumns";

/**
 * Sidebar entry "circumscription_contacts" → /circumscription/contacts.
 *
 * The named contact of every active circumscription, and behind each row the
 * zone coordinators who are flagged as contact persons. Two lists, because the
 * circumscription record carries one address while the people who actually
 * answer are the coordinators.
 */
export function CircumscriptionContactsPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<FilterValues>({});
  const [selected, setSelected] = useState<CircumscriptionContactRow | null>(null);

  const contactColumns = useCircumscriptionContactColumns();
  const coordinatorColumns = useCoordinatorColumns();

  const contacts = useQuery({
    queryKey: ["circumscription", "contacts", filters],
    queryFn: () =>
      apiFetch<ListResponse<CircumscriptionContactRow>>(
        withQuery("/circumscription/circumscription_contacts_grid", filters),
      ),
  });

  const coordinators = useQuery({
    queryKey: ["circumscription", "coordinators", selected?.id],
    queryFn: () =>
      apiFetch<ListResponse<CoordinatorRow>>(
        `/circumscription/circumscription_coordinators_grid/${selected!.id}`,
      ),
    enabled: selected !== null,
  });

  const fields = useMemo(
    () => [
      // One field, two columns: the server matches it against denomination and
      // code alike.
      { name: "circumscription", label: t("activerecord.models.circumscription") },
      { name: "language_code", label: t("activerecord.attributes.language.language_code") },
    ],
    [t],
  );

  return (
    <Card title={t("navigation.circumscription_contacts")}>
      <FilterBar fields={fields} onApply={setFilters} loading={contacts.isFetching} />

      <DataTable
        columns={contactColumns}
        rows={contacts.data?.data}
        loading={contacts.isPending}
        error={contacts.error}
        onRowClick={setSelected}
        exportAs={t("navigation.circumscription_contacts")}
      />

      <Drawer
        open={selected !== null}
        onClose={() => setSelected(null)}
        size={860}
        destroyOnHidden
        title={
          selected && (
            <Typography.Text>
              {[selected.circumscription_code, selected.denomination].filter(Boolean).join(" – ")}
            </Typography.Text>
          )
        }
      >
        <Typography.Title level={5}>{t("circumscription.coordinator")}</Typography.Title>

        {/* Named after the circumscription, not after "coordinators": nothing
            inside the file says which one it belongs to. */}
        <DataTable
          columns={coordinatorColumns}
          rows={coordinators.data?.data}
          exportAs={[selected?.circumscription_code, t("circumscription.coordinator")]
            .filter(Boolean)
            .join(" · ")}
          loading={coordinators.isPending}
          error={coordinators.error}
          paginated={false}
        />
      </Drawer>
    </Card>
  );
}
